import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { changePassword, me } from "../lib/api";

/**
 * Sprint Droits B2 : écran BLOQUANT de changement de mot de passe.
 *
 * Si l'utilisateur courant a `mustChangePassword=true` (compte créé par un admin
 * avec mot de passe initial, ou invité), on l'oblige à définir un nouveau mot de
 * passe avant d'accéder au back-office. Le blocage persiste au refresh (basé sur
 * /admin/auth/me). Une fois changé, le flag tombe et l'app s'affiche.
 */
export function ForcePasswordChange({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const meQuery = useQuery({ queryKey: ["admin", "me"], queryFn: me });
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => changePassword(currentPassword, newPassword),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin", "me"] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Erreur inconnue."),
  });

  // Pendant le 1er chargement du profil, on laisse passer (évite un flash) — le
  // gate s'appliquera dès que la donnée est là.
  if (meQuery.isLoading || !meQuery.data) return <>{children}</>;
  if (!meQuery.data.mustChangePassword) return <>{children}</>;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 10) {
      setError("Le nouveau mot de passe doit faire au moins 10 caractères.");
      return;
    }
    if (newPassword !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    mut.mutate();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[color:var(--color-alto-bg)]">
      <div className="w-full max-w-md bg-white rounded-xl border border-[color:var(--color-alto-border)] p-6 shadow-sm">
        <p className="text-xs uppercase tracking-wider text-[color:var(--color-alto-muted)]">
          Sécurité
        </p>
        <h1 className="text-lg font-semibold mt-1 mb-2">
          Définissez un nouveau mot de passe
        </h1>
        <p className="text-sm text-[color:var(--color-alto-muted)] mb-4">
          Avant d'accéder au back-office, vous devez remplacer le mot de passe
          initial par un mot de passe personnel.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <input
            type="password"
            autoComplete="current-password"
            required
            placeholder="Mot de passe actuel"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-[color:var(--color-alto-border)]"
          />
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            placeholder="Nouveau mot de passe (min. 10 caractères)"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-[color:var(--color-alto-border)]"
          />
          <input
            type="password"
            autoComplete="new-password"
            required
            placeholder="Confirmer le nouveau mot de passe"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-[color:var(--color-alto-border)]"
          />
          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={mut.isPending}
            className="w-full px-4 py-2 rounded-md bg-[color:var(--color-alto-accent)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {mut.isPending ? "Enregistrement…" : "Changer le mot de passe"}
          </button>
        </form>
      </div>
    </div>
  );
}
